import { CommonModule } from '@angular/common';
import { Component, EventEmitter, inject, Input, OnInit, Output } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { NgSelectModule } from '@ng-select/ng-select';
import { SsSelectTooltipDirective } from '../../shared/ss-select-tooltip.directive';
import type { Observable } from 'rxjs';

import {
  cellBadgeFill,
  cellBadgeLookStyle,
  isEmptyTableCellConfig,
  normalizeTableCellConfig,
  parseCellOptionList,
  type TableCellBadgeFill,
  type TableCellConfig,
  type TableCellDisplay,
  type TableCellEditor,
  type TableCellRule,
  type TableCellRuleOp,
  type TableCellValueColor,
} from '../../../chart-components/unified-table-matrix-chart/table-cell-config';
import { DvmBuilderHost } from '../../dvm-layout-builder/dvm-builder-host';
import type { LabelValueOption } from '../../shared/self-service-config.types';

/** Operators that take no compared value. */
const NO_VALUE_OPS: TableCellRuleOp[] = ['empty', 'notEmpty', 'any'];

/** Default colours handed to new value-colour entries, in order (navy, green,
 *  blue, amber, red, violet, teal, brown) — the design's badge palette. */
const VALUE_COLOR_PALETTE = ['#172033', '#3a9b6f', '#6fa8dc', '#e0a21a', '#dc2626', '#7c3aed', '#0e7490', '#9a3412'];

/**
 * The ONE builder editor for a column's generic cell configuration
 * (table-cell-config.ts): display kind, static colours, conditional rules,
 * highlight, prefix / suffix / tooltip and — when `showOptions` — the
 * dropdown values. Dropped into the Normal / Editable column card AND the
 * Drill-Down measure / dimension cards, so every table type is configured
 * the same way and renders through the same resolver.
 *
 * Emits a NORMALIZED config (or `undefined` when nothing is set) on every
 * change; the parent stores it on its column / measure / dimension and
 * refreshes its preview.
 */
@Component({
  selector: 'app-table-cell-config-editor',
  standalone: true,
  imports: [CommonModule, FormsModule, MatIconModule, MatTooltipModule, NgSelectModule, SsSelectTooltipDirective],
  template: `
<div class="tcc-navigation" *ngIf="navigationOnly"><ng-container [ngTemplateOutlet]="navigationSettings"></ng-container></div>
<!-- Stand-alone NAVIGATION accordion (Layout Builder column cards, right
     under the column's name): click a cell value → open a published
     dashboard filtered on that row / a route / a URL. Formatting stays in the
     separate Cell formatting accordion below it. -->
<details *ngIf="navigationAccordion" class="tcc tcc-navigation" [open]="open" (toggle)="open = $any($event.target).open">
  <summary class="tcc-summary">
    <mat-icon class="tcc-summary-icon">link</mat-icon>
    <span class="tcc-summary-title">{{ title }}</span>
    <span class="tcc-summary-state" [class.is-set]="navigationMode !== 'none'">{{ navigationSummaryText }}</span>
    <button *ngIf="navigationMode !== 'none'" type="button" class="tcc-clear" (click)="clearNavigation($event)" matTooltip="Remove this column's navigation">Clear</button>
  </summary>
  <div class="tcc-body"><ng-container [ngTemplateOutlet]="navigationSettings"></ng-container></div>
</details>
<details *ngIf="!navigationOnly && !navigationAccordion" class="tcc" [open]="open" (toggle)="open = $any($event.target).open">
  <summary class="tcc-summary">
    <mat-icon class="tcc-summary-icon">palette</mat-icon>
    <span class="tcc-summary-title">{{ title }}</span>
    <span class="tcc-summary-state" [class.is-set]="!isEmpty">{{ summaryText }}</span>
    <button *ngIf="!isEmpty" type="button" class="tcc-clear" (click)="clearAll($event)" matTooltip="Remove every cell setting on this column">Clear</button>
  </summary>

  <div class="tcc-body">
    <!-- ── Display + static look ─────────────────────────────────────── -->
    <div class="tcc-grid">
      <div class="control-group">
        <label>Show value as</label>
        <ng-select
          #tccDisplay
          class="ss-select"
          [class.is-open]="!!tccDisplay.isOpen?.()"
          appendTo="body"
          ngClass="ss-dropdown-panel"
          [items]="displayOptions"
          bindLabel="label"
          bindValue="value"
          [clearable]="false"
          [searchable]="false"
          [ngModel]="draft.display || 'text'"
          (ngModelChange)="setDisplay($event)"
        ></ng-select>
      </div>
      <div class="control-group" *ngIf="needsAccent">
        <label>{{ accentLabel }}</label>
        <div class="tcc-color">
          <input type="color" [value]="draft.color || '#3b82f6'" (input)="set('color', $any($event.target).value)" />
          <span class="tcc-color-hex">{{ draft.color || 'default' }}</span>
          <button *ngIf="draft.color" type="button" class="tcc-x" (click)="set('color', '')" matTooltip="Use the default colour">✕</button>
        </div>
      </div>
      <div class="control-group" *ngIf="draft.display === 'badge'">
        <label>Badge fill</label>
        <ng-select
          #tccBadgeFill
          class="ss-select"
          [class.is-open]="!!tccBadgeFill.isOpen?.()"
          appendTo="body"
          ngClass="ss-dropdown-panel"
          [items]="badgeFillOptions"
          bindLabel="label"
          bindValue="value"
          [clearable]="false"
          [searchable]="false"
          [ngModel]="effectiveBadgeFill"
          (ngModelChange)="setBadgeFill($event)"
        ></ng-select>
      </div>
    </div>

    <div class="tcc-grid">
      <div class="control-group">
        <label>Text colour</label>
        <div class="tcc-color">
          <input type="color" [value]="draft.textColor || '#172033'" (input)="set('textColor', $any($event.target).value)" />
          <span class="tcc-color-hex">{{ draft.textColor || 'default' }}</span>
          <button *ngIf="draft.textColor" type="button" class="tcc-x" (click)="set('textColor', '')" matTooltip="Default text colour">✕</button>
        </div>
      </div>
      <div class="control-group">
        <label>Background</label>
        <div class="tcc-color">
          <input type="color" [value]="draft.background || '#ffffff'" (input)="set('background', $any($event.target).value)" />
          <span class="tcc-color-hex">{{ draft.background || 'none' }}</span>
          <button *ngIf="draft.background" type="button" class="tcc-x" (click)="set('background', '')" matTooltip="No background">✕</button>
        </div>
      </div>
    </div>

    <div class="tcc-toggles">
      <label class="tm-editable-toggle" matTooltip="Bold text in every cell of this column">
        <input type="checkbox" [ngModel]="!!draft.bold" (ngModelChange)="set('bold', $event)" /><span>Bold</span>
      </label>
      <label class="tm-editable-toggle" matTooltip="Italic text">
        <input type="checkbox" [ngModel]="!!draft.italic" (ngModelChange)="set('italic', $event)" /><span>Italic</span>
      </label>
      <label class="tm-editable-toggle" matTooltip="Long values wrap onto more lines (the row grows) instead of being cut off">
        <input type="checkbox" [ngModel]="!!draft.wrap" (ngModelChange)="set('wrap', $event)" /><span>Wrap text</span>
      </label>
      <label class="tm-editable-toggle" matTooltip="Soft highlight of the whole cell (tint + left accent bar)">
        <input type="checkbox" [ngModel]="!!draft.highlight" (ngModelChange)="set('highlight', $event)" /><span>Highlight</span>
      </label>
      <span class="tcc-color tcc-color-inline" *ngIf="draft.highlight">
        <input type="color" [value]="draft.highlightColor || '#fff7cc'" (input)="set('highlightColor', $any($event.target).value)" matTooltip="Highlight tint" />
      </span>
      <label class="tm-editable-toggle" matTooltip="Money column: shown × the rate of the currency picked in the dashboard's currency dropdown, with its symbol. Display only.">
        <input type="checkbox" [ngModel]="!!draft.convertCurrency" (ngModelChange)="set('convertCurrency', $event)" /><span>Convert currency</span>
      </label>
    </div>

    <div class="tcc-grid tcc-grid-3">
      <div class="control-group">
        <label>Prefix</label>
        <input type="text" [ngModel]="draft.prefix || ''" (ngModelChange)="set('prefix', $event)" placeholder="e.g. $" />
      </div>
      <div class="control-group">
        <label>Suffix</label>
        <input type="text" [ngModel]="draft.suffix || ''" (ngModelChange)="set('suffix', $event)" placeholder="e.g. %" />
      </div>
      <div class="control-group">
        <label>Tooltip</label>
        <input type="text" [ngModel]="draft.tooltip || ''" (ngModelChange)="set('tooltip', $event)" placeholder="{value} and {column} tokens" />
      </div>
    </div>

    <!-- ── On click: navigate — to one of the PUBLISHED dashboards (filtered
         on the clicked row), a route, or a URL link. Available for EVERY
         table type and every display kind: the whole cell value is clickable
         (link displays render an anchor). -->
    <div class="tcc-section tcc-navigation" *ngIf="showNavigation">
      <ng-container [ngTemplateOutlet]="navigationSettings"></ng-container>
    </div>

    <!-- ── Cell input (editor) — only where the host has no input-type control of its own ── -->
    <div class="tcc-section" *ngIf="showEditor">
      <div class="control-group">
        <label>Cell input</label>
        <ng-select
          #tccEditor
          class="ss-select"
          [class.is-open]="!!tccEditor.isOpen?.()"
          appendTo="body"
          ngClass="ss-dropdown-panel"
          [items]="editorOptions"
          bindLabel="label"
          bindValue="value"
          [clearable]="false"
          [searchable]="false"
          [ngModel]="draft.editor || 'none'"
          (ngModelChange)="setEditor($event)"
        ></ng-select>
        <small class="control-hint">Lets users type or pick a value in this column's cells.</small>
      </div>
    </div>

    <!-- ── Dropdown values (cell input) ──────────────────────────────── -->
    <div class="tcc-section" *ngIf="showOptions || draft.editor === 'dropdown'">
      <div class="control-group">
        <label>Dropdown values</label>
        <input
          type="text"
          [ngModel]="optionsText"
          (ngModelChange)="setOptions($event)"
          [placeholder]="valueKind === 'number' ? 'e.g. 0, 25, 50, 100' : 'e.g. Approved, Pending, Rejected'"
        />
        <small class="control-hint">Comma-separated custom values — they don't have to exist in the data.</small>
        <label class="tm-editable-toggle py-2" matTooltip="Adds every value this column currently holds to the dropdown, after the custom values">
          <input type="checkbox" [ngModel]="!!draft.optionsFromData" (ngModelChange)="set('optionsFromData', $event)" /><span>Also list values from the data</span>
        </label>
      </div>
      <div class="control-group">
        <label>Placeholder</label>
        <input type="text" [ngModel]="draft.placeholder || ''" (ngModelChange)="set('placeholder', $event)" placeholder="Shown on the blank option" />
      </div>
    </div>

    <!-- ── Value colours: a flat value → colour map (OEM Position: MFN navy,
         Strategic OEM green, Close partners blue…). Painted as the badge
         colour when the display is a badge, else as the text colour (and the
         dot / bar accent). A matching conditional rule still overrides. -->
    <div class="tcc-section">
      <div class="tcc-rules-head">
        <label>Value colours</label>
        <span class="tcc-head-actions">
          <button type="button" class="tcc-add" *ngIf="loadValues && !dataValues.length" (click)="fetchValues()" [disabled]="loadingValues" matTooltip="Read this column's distinct values from the dataset">{{ loadingValues ? 'Loading…' : '↓ From dataset' }}</button>
          <button type="button" class="tcc-add" *ngIf="missingDataValues.length" (click)="addValuesFromData()" [matTooltip]="'Add the ' + missingDataValues.length + ' value(s) of this column not listed yet, each with its own colour'">+ All values</button>
          <button type="button" class="tcc-add" (click)="addValueColor()">+ Add value</button>
        </span>
      </div>
      <small class="control-hint" *ngIf="!valueColors.length">Give each distinct value of this column its own colour — e.g. MFN navy, Strategic OEM green. With "Badge / pill" every value becomes a chip filled in its colour; with plain text the value's text takes the colour.{{ dataValues.length ? ' "+ All values" adds every value of the column at once.' : (loadValues ? ' "From dataset" reads the column\\'s values so you can pick instead of type.' : '') }}</small>
      <small class="control-hint" role="alert" *ngIf="valuesError">{{ valuesError }}</small>
      <!-- One compact card per value, stacked so it fits the narrow inspector:
           the value picker gets its own full-width line (the dropdown panel
           inherits that width), then colour / text / preview, then badge text. -->
      <div class="tcc-value-color" *ngFor="let entry of valueColors; let i = index">
        <div class="tcc-vc-row">
          <!-- The value: picked from the column's values when the host supplied
               them (typing a value not in the data is still allowed), else typed. -->
          <ng-select
            *ngIf="valueItems.length; else valueTextBox"
            #tccVcValue
            class="ss-select tcc-vc-value"
            [class.is-open]="!!tccVcValue.isOpen?.()"
            appendTo="body"
            ngClass="ss-dropdown-panel tcc-vc-panel"
            [items]="valueItems"
            [addTag]="true"
            addTagText="Use typed value"
            [clearable]="false"
            [searchable]="true"
            placeholder="Pick a value"
            [ngModel]="entry.value || null"
            (ngModelChange)="setValueColor(entry, 'value', $event || '')"
          ></ng-select>
          <ng-template #valueTextBox>
            <input class="tcc-rule-text tcc-vc-value" type="text" [ngModel]="entry.value" (ngModelChange)="setValueColor(entry, 'value', $event)" placeholder="Value, e.g. MFN" matTooltip="The cell value this colour applies to (case-insensitive)" />
          </ng-template>
          <button type="button" class="tcc-x tcc-x-del" (click)="removeValueColor(i)" matTooltip="Remove this value">✕</button>
        </div>
        <div class="tcc-vc-row tcc-vc-look">
          <span class="tcc-color" matTooltip="Colour for this value">
            <span class="tcc-vc-cap">Colour</span>
            <input type="color" [value]="entry.color || '#3b82f6'" (input)="setValueColor(entry, 'color', $any($event.target).value)" />
          </span>
          <span class="tcc-color" *ngIf="isSolidBadge" matTooltip="Text colour over the filled chip (blank = automatic white / dark)">
            <span class="tcc-vc-cap">Text</span>
            <input type="color" [value]="entry.textColor || '#ffffff'" (input)="setValueColor(entry, 'textColor', $any($event.target).value)" />
            <button *ngIf="entry.textColor" type="button" class="tcc-x" (click)="setValueColor(entry, 'textColor', '')" matTooltip="Automatic text colour">✕</button>
          </span>
          <span class="tcc-vc-preview" [ngStyle]="valueColorPreview(entry)" [matTooltip]="entry.label || entry.value">{{ entry.label || entry.value || 'Aa' }}</span>
        </div>
        <div class="tcc-vc-row" *ngIf="draft.display === 'badge'">
          <span class="tcc-vc-cap">Badge text</span>
          <input class="tcc-rule-text tcc-vc-label" type="text" [ngModel]="entry.label || ''" (ngModelChange)="setValueColor(entry, 'label', $event)" placeholder="blank = the value" matTooltip="Text shown on the chip (blank = the value)" />
        </div>
      </div>
    </div>

    <!-- ── Conditional rules ─────────────────────────────────────────── -->
    <div class="tcc-section">
      <div class="tcc-rules-head">
        <label>Conditional formatting</label>
        <button type="button" class="tcc-add" (click)="addRule()">+ Add rule</button>
      </div>
      <small class="control-hint" *ngIf="!rules.length">Colour, bold or badge a cell when its value (or another column's value) meets a condition. The first matching rule wins.</small>

      <div class="tcc-rule" *ngFor="let rule of rules; let i = index">
        <div class="tcc-rule-head">
          <span class="tcc-rule-index">{{ i + 1 }}</span>
          <button type="button" class="tcc-x" (click)="moveRule(i, -1)" [disabled]="i === 0" matTooltip="Move up">↑</button>
          <button type="button" class="tcc-x" (click)="moveRule(i, 1)" [disabled]="i === rules.length - 1" matTooltip="Move down">↓</button>
          <span class="tcc-spacer"></span>
          <button type="button" class="tcc-x tcc-x-del" (click)="removeRule(i)" matTooltip="Remove rule">✕</button>
        </div>
        <div class="tcc-grid tcc-grid-3">
          <div class="control-group" *ngIf="fieldItems.length">
            <label>Based on</label>
            <ng-select
              #tccRuleField
              class="ss-select"
              [class.is-open]="!!tccRuleField.isOpen?.()"
              appendTo="body"
              ngClass="ss-dropdown-panel"
              [items]="fieldItems"
              [clearable]="true"
              [searchable]="true"
              placeholder="This column"
              [ngModel]="rule.field || null"
              (ngModelChange)="setRule(rule, 'field', $event)"
            ></ng-select>
          </div>
          <div class="control-group">
            <label>Condition</label>
            <ng-select
              #tccRuleOp
              class="ss-select"
              [class.is-open]="!!tccRuleOp.isOpen?.()"
              appendTo="body"
              ngClass="ss-dropdown-panel"
              [items]="operatorOptions"
              bindLabel="label"
              bindValue="value"
              [clearable]="false"
              [searchable]="false"
              [ngModel]="rule.op"
              (ngModelChange)="setRule(rule, 'op', $event)"
            ></ng-select>
          </div>
          <div class="control-group" *ngIf="ruleTakesValue(rule)">
            <label>{{ rule.op === 'between' ? 'From' : (rule.op === 'in' || rule.op === 'notIn' ? 'Values (comma-separated)' : 'Value') }}</label>
            <input type="text" [ngModel]="rule.value ?? ''" (ngModelChange)="setRule(rule, 'value', $event)" [placeholder]="valueKind === 'number' ? 'e.g. 0' : 'e.g. Pending'" />
          </div>
          <div class="control-group" *ngIf="rule.op === 'between'">
            <label>To</label>
            <input type="text" [ngModel]="rule.value2 ?? ''" (ngModelChange)="setRule(rule, 'value2', $event)" placeholder="e.g. 100" />
          </div>
        </div>
        <div class="tcc-rule-look">
          <span class="tcc-color" matTooltip="Text colour when matched">
            <input type="color" [value]="rule.color || '#dc2626'" (input)="setRule(rule, 'color', $any($event.target).value)" />
            <span class="tcc-color-hex">{{ rule.color || 'text' }}</span>
            <button *ngIf="rule.color" type="button" class="tcc-x" (click)="setRule(rule, 'color', '')">✕</button>
          </span>
          <span class="tcc-color" matTooltip="Background when matched">
            <input type="color" [value]="rule.background || '#fee2e2'" (input)="setRule(rule, 'background', $any($event.target).value)" />
            <span class="tcc-color-hex">{{ rule.background || 'bg' }}</span>
            <button *ngIf="rule.background" type="button" class="tcc-x" (click)="setRule(rule, 'background', '')">✕</button>
          </span>
          <label class="tm-editable-toggle"><input type="checkbox" [ngModel]="!!rule.bold" (ngModelChange)="setRule(rule, 'bold', $event)" /><span>Bold</span></label>
          <input class="tcc-rule-text" type="text" [ngModel]="rule.icon || ''" (ngModelChange)="setRule(rule, 'icon', $event)" placeholder="Icon ▲ ✓ ⚠" matTooltip="Glyph shown before the value when matched" />
          <input class="tcc-rule-text tcc-rule-label" type="text" *ngIf="draft.display === 'badge'" [ngModel]="rule.label || ''" (ngModelChange)="setRule(rule, 'label', $event)" placeholder="Badge text (blank = value)" />
        </div>
      </div>
    </div>
  </div>
</details>
<ng-template #navigationSettings>
    <div class="tcc-grid">
      <div class="control-group">
        <label>{{ navigationOnly ? 'Navigation' : 'On click' }}</label>

        <select aria-label="Link action" [ngModel]="navigationMode" (ngModelChange)="setNavigationMode($event)">
          <option value="none">None</option>
          <option *ngIf="navigationOnly || draft.display === 'link'" value="url">URL link</option>
          <option value="dashboard">Published dashboard (filtered by this row)</option>
          <option value="route">Route (button navigation)</option>
        </select>
        <small *ngIf="!navigationOnly && navigationMode === 'none'">Clicking a value in this column does nothing. Choose "Published dashboard" to open one of your published dashboards filtered on the clicked row.</small>
      </div>
      <div class="control-group" *ngIf="navigationMode === 'url'">
        <label>Link URL</label><input aria-label="Link URL" [ngModel]="draft.linkTemplate || ''" (ngModelChange)="set('linkTemplate', $event)" placeholder="/customer/{customerId} — blank = the value" />
      </div>
      <ng-container *ngIf="navigationMode === 'dashboard'">
        <div class="control-group">
          <label>Target dashboard</label>
          <ng-select
            *ngIf="dashboardOptions.length; else slugInput"
            #tccNavDash
            class="ss-select"
            [class.is-open]="!!tccNavDash.isOpen?.()"
            appendTo="body"
            ngClass="ss-dropdown-panel"
            [items]="dashboardOptions"
            bindLabel="label"
            bindValue="value"
            [clearable]="true"
            [searchable]="true"
            placeholder="Pick a published dashboard"
            [ngModel]="draft.navigation?.targetDashboard || null"
            (ngModelChange)="setDashboardNavigation('targetDashboard', $event || '')"
          ></ng-select>
          <ng-template #slugInput>
            <input aria-label="Target dashboard slug" [ngModel]="draft.navigation?.targetDashboard || ''" (ngModelChange)="setDashboardNavigation('targetDashboard', $event)" placeholder="Published dashboard slug, e.g. sales-overview" />
            <small>No published dashboards listed yet — type the dashboard's URL slug (the part after /dvm-published/).</small>
          </ng-template>
        </div>
        <div class="control-group">
          <label>Filter field (blank = this column; number columns use the first column)</label>
          <ng-select
            *ngIf="fieldItems.length; else filterInput"
            #tccNavField
            class="ss-select"
            [class.is-open]="!!tccNavField.isOpen?.()"
            appendTo="body"
            ngClass="ss-dropdown-panel"
            [items]="fieldItems"
            [clearable]="true"
            [searchable]="true"
            placeholder="Default — this column, or the first column for numbers"
            [ngModel]="draft.navigation?.filterKey || null"
            (ngModelChange)="setDashboardNavigation('filterKey', $event || '')"
          ></ng-select>
          <ng-template #filterInput><input aria-label="Filter field" [ngModel]="draft.navigation?.filterKey || ''" (ngModelChange)="setDashboardNavigation('filterKey', $event)" /></ng-template>
          <small>The clicked row's value of this field is sent to the target dashboard as its filter. Left blank, a text column sends its own value and a number column (cost, revenue…) sends the row's first column, since a dashboard filters on a key, not an amount.</small>
        </div>
        <div class="control-group" *ngIf="fieldItems.length">
          <label>Also send (more columns of the clicked row)</label>
          <ng-select
            #tccNavExtra
            class="ss-select ss-select--multi"
            [class.is-open]="!!tccNavExtra.isOpen?.()"
            appendTo="body"
            ngClass="ss-dropdown-panel"
            [items]="fieldItems"
            [multiple]="true"
            [closeOnSelect]="false"
            [clearable]="true"
            [searchable]="true"
            placeholder="e.g. customer spec"
            [ngModel]="draft.navigation?.filterKeys || []"
            (ngModelChange)="setDashboardNavigation('filterKeys', $event || [])"
          ></ng-select>
          <small>Each is sent with the row's value, so the next screen gets every filter at once (e.g. customer name AND customer spec). Its dropdowns for those columns select them automatically.</small>
        </div>
        <div class="control-group">
          <label>Target components (blank = all charts)</label>
          <ng-select
            *ngIf="componentOptions.length; else componentsInput"
            #tccNavComps
            class="ss-select ss-select--multi"
            [class.is-open]="!!tccNavComps.isOpen?.()"
            appendTo="body"
            ngClass="ss-dropdown-panel"
            [items]="componentOptions"
            bindLabel="label"
            bindValue="value"
            [multiple]="true"
            [closeOnSelect]="false"
            [hideSelected]="false"
            [clearable]="true"
            [searchable]="true"
            placeholder="All charts on the target dashboard"
            [ngModel]="draft.navigation?.targetComponents || []"
            (ngModelChange)="setDashboardNavigation('targetComponents', $event || [])"
          ></ng-select>
          <ng-template #componentsInput><input aria-label="Target component IDs" [ngModel]="draft.navigation?.targetComponents?.join(', ') || ''" (change)="setDashboardNavigation('targetComponents', $any($event.target).value)" placeholder="Component IDs, comma separated" /></ng-template>
        </div>
        <label><input type="checkbox" [ngModel]="!!draft.navigation?.openInNewTab" (ngModelChange)="setDashboardNavigation('openInNewTab', $event)" />Open in new tab</label>
      </ng-container>
      <ng-container *ngIf="navigationMode === 'route'">
        <div class="control-group"><label>Route</label><input aria-label="Navigation route" [ngModel]="draft.navigateAction?.['route'] || ''" (ngModelChange)="setRouteNavigation('route', $event)" placeholder="/customer/$row.id" /></div>
        <details *ngIf="navigationOnly" class="tcc-nav-advanced">
          <summary>Query parameters &amp; state</summary>
          <ng-container [ngTemplateOutlet]="routeMappings"></ng-container>
        </details>
        <ng-container *ngIf="!navigationOnly" [ngTemplateOutlet]="routeMappings"></ng-container>
        <label><input type="checkbox" [ngModel]="!!draft.navigateAction?.['openInNewTab']" (ngModelChange)="setRouteNavigation('openInNewTab', $event)" />Open in new tab</label>
        <small *ngIf="navigationError" role="alert">{{ navigationError }}</small>
      </ng-container>
    </div>

</ng-template>
<ng-template #routeMappings>
  <div class="control-group"><label>Query parameters (JSON object)</label><textarea aria-label="Navigation query parameters" [ngModel]="navigationJson('queryParams')" (change)="setNavigationJson('queryParams', $any($event.target).value)"></textarea></div>
  <div class="control-group"><label>State (JSON object)</label><textarea aria-label="Navigation state" [ngModel]="navigationJson('state')" (change)="setNavigationJson('state', $any($event.target).value)"></textarea></div>
  <small>Use $event.value for this cell and $row.field or $event.raw.field for the clicked row. Existing button payload, stateKey and listener settings are preserved.</small>
</ng-template>
`,
  styles: [`
    /* The editor sizes itself to its host (a Layout Builder column card, the
       Layout Designer's ~180px column card…): the container query below
       collapses the two-column grids and hides the hex read-outs when narrow. */
    :host { display: block; font-size: 12px; min-width: 0; container-type: inline-size; }
    .tcc-navigation .tcc-grid { grid-template-columns: minmax(0, 1fr); gap: 10px; }
    .tcc-navigation .control-group { display: flex; flex-direction: column; gap: 4px; min-width: 0; }
    .tcc-navigation label { color: #344054; font-size: 11px; line-height: 1.4; font-weight: 500; }
    .tcc-navigation input:not([type='checkbox']), .tcc-navigation select, .tcc-navigation textarea { width: 100%; min-width: 0; box-sizing: border-box; padding: 6px 8px; border: 1px solid #d0d5dd; border-radius: 5px; background: #fff; color: #344054; font: inherit; }
    .tcc-navigation input:not([type='checkbox']), .tcc-navigation select { height: 32px; }
    .tcc-navigation textarea { min-height: 64px; resize: vertical; }
    .tcc-navigation input:focus-visible, .tcc-navigation select:focus-visible, .tcc-navigation textarea:focus-visible { outline: 2px solid #2970ff; outline-offset: 1px; }
    .tcc-navigation input[type='checkbox'] { width: 14px !important; height: 14px !important; min-width: 0 !important; margin: 0 6px 0 0; vertical-align: middle; }
    .tcc-navigation small { color: #667085; font-size: 11px; line-height: 1.4; overflow-wrap: anywhere; }
    .tcc-navigation [role='alert'] { color: #b42318; }
    .tcc-nav-advanced { border-top: 1px solid #eaecf0; padding-top: 8px; }
    .tcc-nav-advanced summary { font-size: 11px; color: #475467; cursor: pointer; }
    .tcc-nav-advanced .control-group { margin-top: 8px; }
    .tcc-nav-advanced small { display: block; margin-top: 6px; }
    /* Self-contained field styling so the editor reads the same inside the
       Layout Builder (which also has global .control-group rules) and the
       Design Builder inspector (which has none). */
    .tcc .control-group { display: flex; flex-direction: column; gap: 3px; margin: 0; min-width: 0; }
    .tcc .control-group > label { font-size: 11px; font-weight: 600; color: #4e5f78; }
    .tcc .control-group > input[type='text'] { height: 28px; padding: 0 8px; border: 1px solid #d0d5dd; border-radius: 4px; font: inherit; color: inherit; background: #fff; }
    .tcc .control-group > input[type='text']:focus { outline: 0; border-color: #2970ff; }
    .tcc .control-hint { font-size: 11px; color: #667085; }
    .tcc .tm-editable-toggle { display: inline-flex; align-items: center; gap: 5px; width: auto; font-size: 11px; line-height: 1.3; color: #344054; cursor: pointer; white-space: nowrap; }
    /* The app's global \`input { width: 100% … }\` rule (styles.scss) would
       stretch a bare checkbox to the panel width inside the Design Builder
       inspector; pin every checkbox in this editor to its natural size. */
    .tcc input[type='checkbox'] { flex: 0 0 auto; width: 14px !important; height: 14px !important; min-width: 0 !important; min-height: 0 !important; margin: 0; padding: 0; accent-color: #1f6feb; cursor: pointer; }
    .tcc input[type='color'] { width: 34px !important; height: 26px !important; min-width: 0 !important; min-height: 0 !important; flex: 0 0 auto; }
    .tcc { border: 1px solid rgba(145, 181, 228, .55); border-radius: 8px; background: rgba(246, 249, 253, .8); }
    .tcc[open] { background: #fff; }
    .tcc-summary { display: flex; align-items: center; gap: 8px; padding: 6px 10px; cursor: pointer; list-style: none; font-size: 12px; font-weight: 600; color: #1e3a5f; }
    .tcc-summary::-webkit-details-marker { display: none; }
    .tcc-summary-icon { font-size: 16px; width: 16px; height: 16px; line-height: 16px; color: #3b6fb6; }
    .tcc-summary-title { flex: 0 0 auto; }
    .tcc-summary-state { flex: 1 1 auto; font-weight: 500; color: #6b7a90; font-size: 11px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
    .tcc-summary-state.is-set { color: #1f6feb; }
    .tcc-clear { border: none; background: transparent; color: #9a3412; font-size: 11px; cursor: pointer; padding: 2px 6px; border-radius: 4px; }
    .tcc-clear:hover { background: rgba(154, 52, 18, .08); }
    .tcc-body { padding: 8px 10px 10px; display: grid; gap: 8px; border-top: 1px solid rgba(145, 181, 228, .35); }
    .tcc-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 8px; }
    .tcc-grid-3 { grid-template-columns: repeat(auto-fit, minmax(120px, 1fr)); }
    .tcc-grid .control-group { margin: 0; min-width: 0; }
    .tcc-color { display: inline-flex; align-items: center; gap: 6px; min-width: 0; }
    .tcc-color input[type='color'] { width: 34px; height: 26px; padding: 0; border: 1px solid rgba(120, 140, 170, .4); border-radius: 6px; background: #fff; cursor: pointer; }
    .tcc-color-hex { font-size: 11px; color: #4b5a6e; font-family: ui-monospace, SFMono-Regular, Menlo, monospace; min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
    .tcc-x { border: 1px solid rgba(120, 140, 170, .35); background: #fff; border-radius: 4px; font-size: 10px; line-height: 1; padding: 3px 5px; cursor: pointer; color: #4b5a6e; }
    .tcc-x:disabled { opacity: .35; cursor: default; }
    .tcc-x-del { color: #b91c1c; }
    .tcc-toggles { display: flex; flex-wrap: wrap; align-items: center; gap: 6px; }
    .tcc-section { border-top: 1px dashed rgba(145, 181, 228, .5); padding-top: 8px; display: grid; gap: 6px; }
    .tcc-rules-head { display: flex; align-items: center; justify-content: space-between; gap: 8px; }
    .tcc-rules-head label { flex: 1 1 auto; min-width: 0; margin: 0; font-size: 11px; font-weight: 600; line-height: 1.3; color: #4e5f78; }
    /* Buttons: the app's global \`button\` rule sets a 14px font + 20px line
       height, which made "+ Add rule" wrap into a tall two-line box inside
       the narrow inspector. Pin the editor's buttons to a compact one-liner. */
    .tcc-add { flex: 0 0 auto; border: 1px solid rgba(39, 125, 242, .45); background: #fff; color: #1f6feb; border-radius: 6px; padding: 3px 9px; font-size: 11px; font-weight: 600; line-height: 1.3; height: auto; min-height: 0; white-space: nowrap; cursor: pointer; }
    .tcc-x, .tcc-clear { line-height: 1.2; height: auto; min-height: 0; white-space: nowrap; }
    .tcc label { font-size: 11px; line-height: 1.3; }
    .tcc .control-hint { font-size: 11px; line-height: 1.35; }
    .tcc-add:hover { background: rgba(39, 125, 242, .06); }
    .tcc-rule { border: 1px solid rgba(120, 140, 170, .3); border-radius: 8px; padding: 6px 8px 8px; display: grid; gap: 6px; background: rgba(248, 250, 252, .9); }
    .tcc-rule-head { display: flex; align-items: center; gap: 4px; }
    .tcc-rule-index { font-size: 11px; font-weight: 700; color: #1f6feb; background: rgba(39, 125, 242, .1); border-radius: 999px; width: 18px; height: 18px; display: inline-flex; align-items: center; justify-content: center; }
    .tcc-spacer { flex: 1 1 auto; }
    .tcc-rule-look { display: flex; flex-wrap: wrap; align-items: center; gap: 8px; }
    .tcc-rule-text { width: 110px; font-size: 12px; padding: 4px 6px; }
    .tcc-rule-label { width: 160px; }
    /* Section heads: the label never breaks letter-by-letter in a narrow
       inspector; the action buttons wrap under it instead. */
    .tcc-rules-head { flex-wrap: wrap; }
    .tcc-rules-head label { white-space: nowrap; flex: 1 1 auto; }
    .tcc-head-actions { display: inline-flex; flex-wrap: wrap; gap: 4px; margin-left: auto; }
    /* Value colour cards: two short rows each, so they fit ~240px panels. */
    .tcc-value-color { display: grid; gap: 5px; padding: 6px 8px; border: 1px solid rgba(120, 140, 170, .3); border-radius: 8px; background: rgba(248, 250, 252, .9); min-width: 0; overflow: hidden; }
    .tcc-vc-row { display: flex; align-items: center; gap: 6px; min-width: 0; }
    /* Swatches have fixed widths, so this row WRAPS rather than overflow the
       card: colour, text and the preview chip fall onto new lines as needed. */
    .tcc-vc-look { flex-wrap: wrap; row-gap: 4px; }
    /* The picker takes the whole line (minus the remove button) so its text
       and its body-level dropdown panel are readable in a narrow inspector. */
    .tcc-vc-value { flex: 1 1 auto; min-width: 0; width: auto; }
    .tcc-vc-cap { flex: 0 0 auto; font-size: 10px; font-weight: 600; color: #6b7a90; text-transform: uppercase; letter-spacing: .03em; white-space: nowrap; }
    .tcc-vc-label { flex: 1 1 auto; min-width: 0; width: auto; }
    .tcc-vc-preview { display: inline-block; flex: 0 1 auto; min-width: 0; max-width: 100%; margin-left: auto; padding: 1px 9px; border-radius: 999px; font-size: 11px; font-weight: 600; line-height: 1.6; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
    /* Narrow hosts (the Layout Designer's column cards): one control per
       line, no hex read-outs, full-width rule inputs. */
    @container (max-width: 300px) {
      .tcc-grid, .tcc-grid-3 { grid-template-columns: minmax(0, 1fr); }
      .tcc-color-hex { display: none; }
      .tcc-rule-text, .tcc-rule-label { width: 100%; }
      .tcc-rule-look { gap: 6px; }
      .tcc-vc-preview { margin-left: 0; }
    }
  `],
})
export class TableCellConfigEditorComponent implements OnInit {
  private readonly host = inject(DvmBuilderHost, { optional: true });

  /** Design inspector: ONLY the navigation controls, no card (links render as anchors). */
  @Input() navigationOnly = false;
  /** Layout Builder column cards: the navigation controls as their OWN
   *  accordion (title "Navigation"); the display kind is left untouched. */
  @Input() navigationAccordion = false;
  /** Full editor: show the "On click" block inside the formatting card.
   *  Hosts that render the navigation accordion separately pass false. */
  @Input() showNavigation = true;
  /** Card title (e.g. "Cell formatting"). */
  @Input() title = 'Cell formatting';
  /** Other columns of the table, for rules that test another column. */
  @Input() set fields(value: string[] | null | undefined) {
    const next = Array.isArray(value) ? value.filter(Boolean) : [];
    if (next.join('\u0000') !== this.fieldItems.join('\u0000')) this.fieldItems = next;
  }
  /** Distinct values this column holds in the data (hosts that have rows at
   *  hand pass them): enables "+ From data" under Value colours. */
  @Input() set values(value: string[] | null | undefined) {
    const next = Array.isArray(value) ? Array.from(new Set(value.map((v) => String(v ?? '').trim()).filter(Boolean))) : [];
    if (next.join('\u0000') !== this.dataValues.join('\u0000')) {
      this.dataValues = next;
      if (next.length) this.valuesError = '';
      this.refreshValueItems();
    }
  }
  /** Hosts without rows at hand (Layout Builder step cards) pass a loader
   *  that reads the column's distinct values from the dataset on demand
   *  ("From dataset" button). */
  @Input() loadValues: (() => Observable<string[]>) | null = null;
  /** Show the dropdown-values block (the column's cells edit via a select). */
  @Input() showOptions = false;
  /** Show the "Cell input" select (none / text / number / dropdown / …) — for
   *  hosts that have no input-type control of their own (nested table). */
  @Input() showEditor = false;
  /** Hint for placeholders: number vs text column. */
  @Input() valueKind: 'number' | 'text' = 'number';
  @Input() set config(value: TableCellConfig | null | undefined) {
    // The parent may hand back a re-normalised COPY of what we just emitted
    // (Design tab overrides, nested level overrides). Treat it as our own
    // echo, else the draft is re-cloned mid-typing and the "Dropdown values"
    // text loses its trailing comma / space on every keystroke.
    if (value === this.lastEmitted) return;
    if (this.lastEmitted !== undefined && JSON.stringify(normalizeTableCellConfig(value) ?? null) === JSON.stringify(this.lastEmitted ?? null)) return;
    if (this.lastEmitted === undefined && !normalizeTableCellConfig(value) && this.draftTouched) return;
    this.draft = this.cloneConfig(value);
    this.optionsText = (this.draft.options || []).join(', ');
    this.refreshValueItems();
    if (this.navigationAccordion ? this.navigationMode !== 'none' : !isEmptyTableCellConfig(this.draft)) this.open = true;
    // A restored target that is no longer among the listed dashboards must
    // still show in the picker.
    const slug = this.draft.navigation?.targetDashboard;
    if (slug) this.host?.ensureNavTargetInList(slug);
  }
  @Output() configChange = new EventEmitter<TableCellConfig | undefined>();

  /** Mutable working copy (rules included). */
  draft: TableCellConfig = {};
  optionsText = '';
  open = false;
  /** Memoized — ng-select with appendTo=body needs a stable [items] reference. */
  fieldItems: string[] = [];
  dataValues: string[] = [];
  /** Value picker items: the data values plus any typed value not among them (stable reference). */
  valueItems: string[] = [];
  loadingValues = false;
  valuesError = '';
  displayOptions: LabelValueOption[] = [];
  operatorOptions: LabelValueOption[] = [];
  editorOptions: LabelValueOption[] = [];
  badgeFillOptions: LabelValueOption[] = [];
  private lastEmitted: TableCellConfig | undefined;
  /** True once the user changed something in this instance. */
  private draftTouched = false;

  private readonly editorFallback: LabelValueOption[] = [
    { label: 'Read-only', value: 'none' },
    { label: 'Text box', value: 'text' },
    { label: 'Number', value: 'number' },
    { label: 'Dropdown', value: 'dropdown' },
    { label: 'Checkbox', value: 'checkbox' },
    { label: 'Date', value: 'date' },
    { label: 'Multi-line text', value: 'textarea' },
  ];

  private readonly displayFallback: LabelValueOption[] = [
    { label: 'Plain text', value: 'text' },
    { label: 'Badge / pill', value: 'badge' },
    { label: 'Data bar', value: 'databar' },
    { label: 'Progress bar (0–100)', value: 'progress' },
    { label: 'Heat map', value: 'heatmap' },
    { label: 'Trend arrow (▲ ▼)', value: 'trend' },
    { label: 'Colour dot + value', value: 'icon' },
    { label: 'Link', value: 'link' },
  ];
  private readonly badgeFillFallback: LabelValueOption[] = [
    { label: 'Soft tint (coloured text)', value: 'soft' },
    { label: 'Solid fill (white / dark text)', value: 'solid' },
  ];
  private readonly operatorFallback: LabelValueOption[] = [
    { label: 'Greater than', value: '>' },
    { label: 'Greater or equal', value: '>=' },
    { label: 'Less than', value: '<' },
    { label: 'Less or equal', value: '<=' },
    { label: 'Equals', value: '=' },
    { label: 'Not equal', value: '!=' },
    { label: 'Between', value: 'between' },
    { label: 'Contains', value: 'contains' },
    { label: 'Does not contain', value: 'notContains' },
    { label: 'Starts with', value: 'startsWith' },
    { label: 'Ends with', value: 'endsWith' },
    { label: 'Is one of', value: 'in' },
    { label: 'Is not one of', value: 'notIn' },
    { label: 'Is empty', value: 'empty' },
    { label: 'Is not empty', value: 'notEmpty' },
    { label: 'Always (catch-all)', value: 'any' },
  ];

  ngOnInit(): void {
    const options = (this.host?.layout?.options as Record<string, unknown> | undefined) || {};
    this.displayOptions = (options['tmCellDisplays'] as LabelValueOption[] | undefined) ?? this.displayFallback;
    this.operatorOptions = (options['tmCellRuleOperators'] as LabelValueOption[] | undefined) ?? this.operatorFallback;
    this.editorOptions = (options['tmCellEditors'] as LabelValueOption[] | undefined) ?? this.editorFallback;
    this.badgeFillOptions = (options['tmBadgeFills'] as LabelValueOption[] | undefined) ?? this.badgeFillFallback;
  }

  get rules(): TableCellRule[] {
    return this.draft.rules || [];
  }
  get valueColors(): TableCellValueColor[] {
    return this.draft.valueColors || [];
  }
  /** The fill the badge renders with (solid once value colours exist). */
  get effectiveBadgeFill(): TableCellBadgeFill {
    return cellBadgeFill(this.draft);
  }
  get isSolidBadge(): boolean {
    return this.draft.display === 'badge' && this.effectiveBadgeFill === 'solid';
  }
  /** Data values not yet listed under Value colours. */
  get missingDataValues(): string[] {
    const listed = new Set(this.valueColors.map((e) => e.value.trim().toLowerCase()));
    return this.dataValues.filter((v) => !listed.has(v.toLowerCase()));
  }
  get isEmpty(): boolean {
    return isEmptyTableCellConfig(normalizeTableCellConfig(this.draft));
  }
  /** Which displays use the accent colour. */
  get needsAccent(): boolean {
    const d = this.draft.display;
    return d === 'badge' || d === 'databar' || d === 'progress' || d === 'heatmap' || d === 'icon';
  }
  get accentLabel(): string {
    switch (this.draft.display) {
      case 'badge': return 'Badge colour';
      case 'heatmap': return 'Heat colour (high end)';
      case 'icon': return 'Dot colour';
      default: return 'Bar colour';
    }
  }
  /** One-line summary for the collapsed card. */
  get summaryText(): string {
    const cfg = normalizeTableCellConfig(this.draft);
    if (!cfg) return 'default';
    const parts: string[] = [];
    if (cfg.display && cfg.display !== 'text') parts.push(this.displayOptions.find((o) => o.value === cfg.display)?.label || cfg.display);
    if (cfg.textColor || cfg.background) parts.push('coloured');
    if (cfg.valueColors?.length) parts.push(`${cfg.valueColors.length} value colour${cfg.valueColors.length === 1 ? '' : 's'}`);
    if (cfg.bold) parts.push('bold');
    if (cfg.highlight) parts.push('highlight');
    if (cfg.rules?.length) parts.push(`${cfg.rules.length} rule${cfg.rules.length === 1 ? '' : 's'}`);
    if (cfg.options?.length || cfg.optionsFromData) parts.push('dropdown values');
    if (cfg.prefix || cfg.suffix) parts.push('prefix/suffix');
    if (cfg.convertCurrency) parts.push('currency');
    if (cfg.tooltip) parts.push('tooltip');
    if (this.showNavigation) {
      if (cfg.navigation) parts.push(cfg.navigation.targetDashboard ? `opens ${cfg.navigation.targetDashboard}` : 'navigation');
      else if (cfg.navigateAction) parts.push('navigation');
    }
    return parts.join(' · ') || 'default';
  }
  /** Summary of the stand-alone Navigation accordion. */
  get navigationSummaryText(): string {
    switch (this.navigationMode) {
      case 'dashboard': return this.draft.navigation?.targetDashboard ? `opens ${this.draft.navigation.targetDashboard}` : 'dashboard — pick one';
      case 'route': return this.draft.navigateAction?.['route'] ? `route ${this.draft.navigateAction['route']}` : 'route — set one';
      case 'url': return 'URL link';
      default: return 'none';
    }
  }
  /** Navigation accordion's Clear: drop every click setting, keep the look. */
  clearNavigation(event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    delete this.draft.navigation;
    delete this.draft.navigateAction;
    delete this.draft.linkTemplate;
    if (this.draft.display === 'link') delete this.draft.display;
    this.navigationError = '';
    this.emit();
  }

  /** Published / saved dashboards a cell can open (from the builder host). */
  get dashboardOptions(): LabelValueOption[] {
    return this.host?.tmNavTargetDashboards ?? [];
  }
  /** Widgets of the chosen target dashboard (empty = all charts). */
  get componentOptions(): LabelValueOption[] {
    const slug = this.draft.navigation?.targetDashboard;
    return slug ? (this.host?.navTargetComponentOptions(slug) ?? []) : [];
  }

  ruleTakesValue(rule: TableCellRule): boolean {
    return !NO_VALUE_OPS.includes(rule.op);
  }

  // ── Mutations (every one emits the normalized config) ───────────────────────
  navigationError = '';
  /** 'dashboard' / 'route' navigate on click whatever the display kind;
   *  'url' is the link display's plain href; 'none' = not clickable. */
  get navigationMode(): string {
    if (this.draft.navigation) return 'dashboard';
    if (this.draft.navigateAction) return 'route';
    return this.draft.display === 'link' ? 'url' : 'none';
  }
  setNavigationMode(mode: string): void {
    delete this.draft.navigation;
    delete this.draft.navigateAction;
    this.navigationError = '';
    if (mode === 'none') {
      if (this.draft.display === 'link') delete this.draft.display;
    } else if (mode === 'url') {
      this.draft.display = 'link';
    } else {
      if (mode === 'dashboard') this.draft.navigation = { targetDashboard: '' };
      if (mode === 'route') this.draft.navigateAction = { route: '' };
      // The Design inspector's navigation-only editor renders every linked
      // column as an anchor; the full editor keeps the chosen display kind
      // (badge, data bar…) and makes the whole cell clickable instead.
      if (this.navigationOnly) this.draft.display = 'link';
    }
    this.emit();
  }
  setDashboardNavigation(key: string, value: unknown): void {
    const nav = { ...(this.draft.navigation ?? { targetDashboard: '' }) };
    const next = key === 'targetComponents' || key === 'filterKeys' ? parseCellOptionList(value) : value;
    const changed = { ...nav, [key]: next } as NonNullable<TableCellConfig['navigation']>;
    // Another dashboard → its widgets differ; start from "all charts".
    if (key === 'targetDashboard' && String(value ?? '') !== String(nav.targetDashboard ?? '')) changed.targetComponents = [];
    this.draft.navigation = changed;
    this.emit();
  }
  setRouteNavigation(key: string, value: unknown): void {
    this.draft.navigateAction = { ...this.draft.navigateAction, [key]: value };
    this.emit();
  }
  navigationJson(key: string): string {
    return JSON.stringify(this.draft.navigateAction?.[key] ?? {}, null, 2);
  }
  setNavigationJson(key: string, text: string): void {
    try {
      const value = JSON.parse(text.trim() || '{}');
      if (!value || typeof value !== 'object' || Array.isArray(value)) throw new Error();
      this.navigationError = '';
      this.setRouteNavigation(key, value);
    } catch { this.navigationError = `${key} must be a valid JSON object.`; }
  }
  setDisplay(value: string | null): void {
    this.draft.display = (value || 'text') as TableCellDisplay;
    this.emit();
  }
  setBadgeFill(value: string | null): void {
    if (value === 'solid' || value === 'soft') this.draft.badgeFill = value;
    else delete this.draft.badgeFill;
    this.emit();
  }
  // ── Value colours ─────────────────────────────────────────────────────────
  addValueColor(): void {
    this.draft.valueColors = [...this.valueColors, { value: '', color: this.nextValueColor(this.valueColors.length) }];
    this.open = true;
    this.emit();
  }
  /** "From dataset": read the column's distinct values through the host's loader. */
  fetchValues(): void {
    if (!this.loadValues || this.loadingValues) return;
    this.loadingValues = true;
    this.valuesError = '';
    this.loadValues().subscribe({
      next: (list) => {
        this.loadingValues = false;
        this.values = list;
        if (!this.dataValues.length) this.valuesError = 'No values found for this column in the dataset.';
      },
      error: () => {
        this.loadingValues = false;
        this.valuesError = 'Could not read the column values from the dataset.';
      },
    });
  }
  private refreshValueItems(): void {
    const items = [...this.dataValues];
    this.valueColors.forEach((entry) => { if (entry.value && !items.includes(entry.value)) items.push(entry.value); });
    if (items.join('\u0000') !== this.valueItems.join('\u0000')) this.valueItems = items;
  }
  /** One entry per data value not listed yet, each with the next palette colour. */
  addValuesFromData(): void {
    const added = this.missingDataValues.map((value, i) => ({ value, color: this.nextValueColor(this.valueColors.length + i) }));
    if (!added.length) return;
    this.draft.valueColors = [...this.valueColors, ...added];
    this.open = true;
    this.emit();
  }
  private nextValueColor(index: number): string {
    return VALUE_COLOR_PALETTE[index % VALUE_COLOR_PALETTE.length];
  }
  removeValueColor(index: number): void {
    this.draft.valueColors = this.valueColors.filter((_e, i) => i !== index);
    if (!this.draft.valueColors.length) delete this.draft.valueColors;
    this.emit();
  }
  setValueColor(entry: TableCellValueColor, key: keyof TableCellValueColor & string, value: unknown): void {
    const text = String(value ?? '');
    if (key === 'value' || key === 'color') entry[key] = text;
    else if (text.trim()) entry[key] = text;
    else delete entry[key];
    this.emit();
  }
  /** Swatch next to an entry: the badge as it will paint, else the text colour. */
  valueColorPreview(entry: TableCellValueColor): Record<string, string> {
    if (this.draft.display === 'badge') return cellBadgeLookStyle(this.draft, { rule: null, highlight: false, valueColor: entry });
    return { color: entry.textColor || entry.color, background: 'rgba(100, 116, 139, 0.08)' };
  }
  setEditor(value: string | null): void {
    if (!value || value === 'none') delete this.draft.editor;
    else this.draft.editor = value as TableCellEditor;
    this.emit();
  }
  set(key: keyof TableCellConfig & string, value: unknown): void {
    if (typeof value === 'boolean') {
      if (value) (this.draft as Record<string, unknown>)[key] = true;
      else delete (this.draft as Record<string, unknown>)[key];
    } else {
      const text = String(value ?? '');
      if (text.trim()) (this.draft as Record<string, unknown>)[key] = text;
      else delete (this.draft as Record<string, unknown>)[key];
    }
    this.emit();
  }
  setOptions(text: string): void {
    this.optionsText = text;
    const list = parseCellOptionList(text);
    if (list.length) this.draft.options = list;
    else delete this.draft.options;
    this.emit();
  }
  addRule(): void {
    const rule: TableCellRule = { op: this.valueKind === 'number' ? '<' : '=', value: this.valueKind === 'number' ? 0 : '', color: '#dc2626' };
    this.draft.rules = [...this.rules, rule];
    this.open = true;
    this.emit();
  }
  removeRule(index: number): void {
    this.draft.rules = this.rules.filter((_r, i) => i !== index);
    if (!this.draft.rules.length) delete this.draft.rules;
    this.emit();
  }
  moveRule(index: number, delta: number): void {
    const next = [...this.rules];
    const target = index + delta;
    if (target < 0 || target >= next.length) return;
    [next[index], next[target]] = [next[target], next[index]];
    this.draft.rules = next;
    this.emit();
  }
  setRule(rule: TableCellRule, key: keyof TableCellRule & string, value: unknown): void {
    const r = rule as unknown as Record<string, unknown>;
    if (key === 'op') {
      r['op'] = String(value || '=');
    } else if (typeof value === 'boolean') {
      if (value) r[key] = true;
      else delete r[key];
    } else {
      const text = String(value ?? '');
      if (text.trim()) r[key] = text;
      else delete r[key];
    }
    this.emit();
  }
  clearAll(event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    // Navigation owned by a separate accordion survives a formatting clear.
    const keep: TableCellConfig = {};
    if (!this.showNavigation) {
      if (this.draft.navigation) keep.navigation = this.draft.navigation;
      if (this.draft.navigateAction) keep.navigateAction = this.draft.navigateAction;
    }
    this.draft = keep;
    this.optionsText = '';
    this.emit();
  }

  private emit(): void {
    this.draftTouched = true;
    this.refreshValueItems();
    const next = normalizeTableCellConfig(this.draft);
    this.lastEmitted = next;
    this.configChange.emit(next);
  }
  private cloneConfig(value: TableCellConfig | null | undefined): TableCellConfig {
    const cfg = normalizeTableCellConfig(value);
    if (!cfg) return {};
    return {
      ...cfg,
      ...(cfg.rules ? { rules: cfg.rules.map((r) => ({ ...r })) } : {}),
      ...(cfg.valueColors ? { valueColors: cfg.valueColors.map((e) => ({ ...e })) } : {}),
    };
  }
}
